/**
 * @pt/fabric — cloth colour data, PBR material sets and the capture pipeline.
 *
 * OWNER: fabric-digitization-specialist. Everyone else reads.
 *
 * ROUND 1 TRUTH (D-019, AS-3): every colour in this book is SYNTHETIC and
 * CLEARLY LABELLED. It is a house rendering of a colour someone described, not
 * a measurement of cloth. `placeholder: true` in `rules.json` drives
 * `ST-BOOK-PLACEHOLDER` and the per-cloth provenance field BY DATA — flipping a
 * seed flag changes both statements with no code edit (RULING-009).
 *
 * D-006: the 2D macro swatch is the COLOUR-TRUTH surface; the 3D view is the
 * SILHOUETTE-TRUTH surface. This package serves both from one weave model, so
 * they can disagree about lighting but never about what the cloth is.
 *
 * WHAT IS NOT HERE. Composition, weight, width, weave name, pattern name,
 * season, occasion, garment suitability, price band and stock are `rules.json`'s
 * and are served by the API. This package adds only what Fabric Digitization
 * owns. A rule input has one source of truth (OPS_SPEC §5.1).
 *
 * The barrel deliberately does NOT import `rules.json`: it reads the small
 * generated manifest instead, so the web bundle carries kilobytes, not a
 * 124 KB rules document.
 */

import { color } from '@pt/tokens';
import manifestJson from './generated/manifest.json' with { type: 'json' };
import type {
  FabricManifest,
  FabricMaterial,
  SwatchImageSpec,
  WeaveClass,
  WeaveTextureSet,
} from './types.ts';

export * from './types.ts';
export * from './colour.ts';
export { CAPTURE_SCALE_PX_PER_CM } from './weave.ts';

/**
 * The asset roots. `/fabric` is what this package GENERATES; `/fabric-real`
 * serves the committed photographs by reference and holds no generated byte.
 * Both are same-origin, no CDN (invariant 16).
 */
export {
  FABRIC_ASSET_ROOT,
  FABRIC_REAL_ASSET_ROOT,
  SWATCH_SOURCE_ROOT,
  servedPathForSource,
  sourceForServedPath,
} from './paths.ts';

export const FABRIC_MANIFEST: FabricManifest = manifestJson as unknown as FabricManifest;
export const FABRIC_MATERIALS: readonly FabricMaterial[] = FABRIC_MANIFEST.materials;
export const WEAVE_TEXTURES: readonly WeaveTextureSet[] = FABRIC_MANIFEST.weaveTextures;

export function fabricMaterial(clothId: string): FabricMaterial | undefined {
  return FABRIC_MATERIALS.find((m) => m.clothId === clothId);
}

export function weaveTexture(weaveClass: WeaveClass): WeaveTextureSet | undefined {
  return WEAVE_TEXTURES.find((w) => w.weaveClass === weaveClass);
}

/**
 * The image spec a 2D surface must render — including the crop it must apply.
 *
 * A photographed cloth is served BY REFERENCE: `path` is the supplier's own
 * file, and only `crop` makes it a swatch. A surface that renders `path` and
 * ignores `crop` shows the client the label chip, the card edge and whatever
 * the photographer's table was made of. Read the whole spec, never just the
 * path.
 */
export function swatchImage(
  clothId: string,
  kind: 'macro' | 'tile' = 'macro',
): SwatchImageSpec | undefined {
  const material = fabricMaterial(clothId);
  if (!material) return undefined;
  return kind === 'macro' ? material.macroSwatch : material.tile;
}

/* ------------------------------------------------ the weave decode, once */

/**
 * The one true reading of a weave map. The 3D shader's GLSL and this function
 * must agree, so the constants live here and are published on the texture
 * record rather than being re-typed on either side.
 *
 *   albedo_linear *= weaveMultiplier(sampled, texture, pbr.weaveAlbedoAmplitude)
 *
 * `sampled` is the texel in 0..1 as a sampler returns it. The map is LINEAR
 * data — load it `NoColorSpace`; decoding it through a transfer curve bends the
 * cloth's colour.
 *
 * The identity that makes this safe at every distance: the map's mean IS
 * `neutralUnit`, so `weaveMultiplier(neutralUnit, …) === 1` exactly, and every
 * mip level therefore averages back to no modulation. A weave that mips down to
 * a colour shift is the commonest way a rendered cloth quietly changes shade as
 * the camera pulls back.
 */
export function weaveMultiplier(
  sampled: number,
  texture: Pick<WeaveTextureSet, 'neutralUnit' | 'halfRangeUnit'>,
  weaveAlbedoAmplitude: number,
): number {
  return 1 + (weaveAlbedoAmplitude * (sampled - texture.neutralUnit)) / texture.halfRangeUnit;
}

/* ------------------------------------------------- RULING-002 adjacency */

/**
 * The ONLY three grounds that may touch cloth (RULING-002, VISUAL_SYSTEM §3.3).
 * Read from the generated token artifact — never re-typed here, so a token
 * change cannot leave a stale copy behind a swatch.
 *
 *   book        the swatch grid and its captions
 *   assessment  the band around the macro swatch; mid-grey, ISO 3664 practice,
 *               because a near-white surround makes a mid-tone cloth read darker
 *               and more saturated than it is
 *   room        the interior of the 3D viewport
 */
export const FABRIC_SURROUND_TOKENS = {
  'color.surface.fabricSurround.book': color.surface.fabricSurround.book,
  'color.surface.fabricSurround.assessment': color.surface.fabricSurround.assessment,
  'color.surface.fabricSurround.room': color.surface.fabricSurround.room,
} as const;

export type FabricSurroundToken = keyof typeof FABRIC_SURROUND_TOKENS;

/** True only for the three legal surrounds. Used by the verification test and
 *  available to any surface that wants to assert its own ground. */
export function isLegalFabricSurround(value: string): boolean {
  return Object.values(FABRIC_SURROUND_TOKENS).some(
    (v) => v.toUpperCase() === value.toUpperCase(),
  );
}

/* -------------------------------------------------- the colour statement */

/**
 * Everything a cloth surface needs to render [T-37]'s uniform "Colour on
 * screen" field and [T-38]'s two never-merged claims. The copy is the UX
 * Writer's; these are the values it is given.
 *
 *   tolerance  (a) how close this rendering is to the cloth. `null` in Round 1.
 *   provenance (b) whether the colour was measured from the cloth at all.
 *
 * `deltaEShown` is what gets recorded on the acknowledgement ([T-46b]); when it
 * is null, the recorded value is null, and the record then says truthfully that
 * no tolerance was shown because none had been measured.
 *
 * INV-COLOUR-01 (rules.json 1.3.2): "No client-facing string may state a ΔE
 * figure ... for a cloth whose deltaEBasis is not a measured basis." So this
 * type carries NO numeric colour target. `houseTargetDeltaE00` exists on the
 * material record and is staff-only; it is deliberately not reachable from the
 * helper the cloth surfaces call, because the cheapest way to be sure a number
 * is never rendered is for the render path never to receive one.
 */
export interface ColourStatementInputs {
  readonly clothId: string;
  readonly colourName: string;
  readonly deltaEShown: number | null;
  readonly provenance: FabricMaterial['colour']['provenance'];
  readonly colourConfidence: FabricMaterial['colour']['colourConfidence'];
  /** Selects the ack text variant: 'unmeasured' unless this is a measured basis. */
  readonly deltaEBasis: FabricMaterial['colour']['deltaEBasis'];
  /**
   * The served file. On a photographed cloth it is the supplier's whole
   * photograph, so **do not render it bare** — take the crop from
   * `swatchImage(clothId)` and window it, or the client sees the label chip.
   */
  readonly macroSwatchPath: string;
}

export function colourStatementInputs(clothId: string): ColourStatementInputs | undefined {
  const m = fabricMaterial(clothId);
  if (!m) return undefined;
  return {
    clothId: m.clothId,
    colourName: m.colourName,
    deltaEShown: m.colour.deltaEShown,
    provenance: m.colour.provenance,
    colourConfidence: m.colour.colourConfidence,
    deltaEBasis: m.colour.deltaEBasis,
    macroSwatchPath: m.macroSwatch.path,
  };
}

/** True while any cloth is still unmeasured — drives the book-level standing
 *  statement (RULING-009). Data, not a hand-set flag. */
export function bookHasUnmeasuredColour(): boolean {
  return FABRIC_MATERIALS.some((m) => m.colour.deltaEBasis !== 'measured_against_cloth');
}

/**
 * INV-COLOUR-01, as a function the render path can call.
 *
 * `rules.json -> acknowledgements[ack.colour_on_screen].variantSelector`: the
 * 'measured' variant is permitted ONLY when the basis records an actual
 * measurement against the physical cloth; any other basis selects 'unmeasured'.
 * Today every cloth returns 'unmeasured', and the 'measured' branch is dead
 * code that must not be selected before Fabric Digitization ships a capture.
 */
export function colourTextVariant(clothId: string): 'measured' | 'unmeasured' {
  return fabricMaterial(clothId)?.colour.deltaEBasis === 'measured_against_cloth'
    ? 'measured'
    : 'unmeasured';
}

/** True if a cloth may carry ANY numeric colour claim on a client surface. */
export function mayStateNumericColourClaim(clothId: string): boolean {
  return colourTextVariant(clothId) === 'measured';
}
